'use client';

/**
 * **A light confirm for a destructive gesture on a record you already have
 * open** (LF-6307 / [ATTR-6]) — a `Popover` anchored to the control that
 * triggers it, rather than a centred modal.
 *
 * ## Why this exists next to `DeleteConfirmationDialog`
 *
 * `DeleteConfirmationDialog` is the registry standard and remains the default.
 * It is a **centred modal**: it blacks out the page and takes over the viewport
 * to ask one yes/no question. On a surface whose whole argument is that it
 * reads as a quiet record rather than a form — the attribute treatment
 * (`docs/attribute-treatment.md`) — that is a much heavier gesture than the one
 * being confirmed, and the page it blacks out is the very record the question
 * is about.
 *
 * So this anchors to the control instead, at the same radius, border and type
 * scale as the surface's other popover editors, and it names the record so the
 * question answers *which* thing is going. The departure is deliberate and is
 * recorded in `.claude/docs/standardised-components.md` — the thing that must
 * not happen is a second confirm pattern spreading with nothing saying why.
 *
 * ## Reach for which
 *
 * - **This** — the destructive gesture is scoped to the record already open in
 *   front of the user, on a surface that is already an editor.
 * - **`DeleteConfirmationDialog`** — bulk or off-surface deletions, anything
 *   the user is not already looking at, or a deletion that needs its own error
 *   surface (see below).
 *
 * ## What it deliberately does not do
 *
 * It has **no error surface**. `onConfirm` may be async and the buttons
 * disable while it is in flight (so a slow delete cannot be double-fired), but
 * a *failure* is the caller's to report — both Contacts hosts already toast
 * their own delete failures, and a second error surface inside a popover that
 * is about to unmount would duplicate them. A destructive action whose failure
 * has nowhere else to go wants `DeleteConfirmationDialog`, which owns one.
 */

import { useState, type ReactElement, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

export interface DeleteConfirmPopoverProps {
  /**
   * The control the popover anchors to — rendered as the trigger via `asChild`,
   * so callers own their own button styling and hit box.
   *
   * A single valid element, not `ReactNode`: `asChild` renders it through a
   * `Slot`, and @radix-ui/react-popover 1.1.23 already resolves
   * @radix-ui/react-slot 1.3.3, which throws on anything else (VALE-1288).
   */
  trigger: ReactElement;
  /**
   * Render `trigger` as a `PopoverAnchor` (position reference only) instead of
   * a `PopoverTrigger` (VALE-1810). Set this when `trigger` already owns its
   * own click-to-open control — a `DropdownMenuTrigger`, most concretely —
   * because `PopoverTrigger` registers its own click-toggle and
   * `aria-haspopup`/`aria-controls` on the same node via `asChild`, and two
   * disclosure primitives both claiming that node fight over it: whichever is
   * outermost wins the aria wiring, and clicking it flips both components'
   * open state at once. `PopoverAnchor` only reads the node's position and
   * leaves the wrapped trigger's own wiring alone, which is all a
   * fully-controlled popover like this one still needs from it.
   */
  anchorOnly?: boolean;
  /**
   * The question, e.g. `Delete John Robert Doe?`. Callers pass the whole
   * sentence rather than a bare name: the two Contacts hosts delete genuinely
   * different things (one removes a contact *from a matter*, the other deletes
   * the contact outright), so the wording is theirs to own.
   */
  title: ReactNode;
  /** The consequence, in one line — what goes, and whether it comes back. */
  description: ReactNode;
  /** Defaults to `Delete`. Use the verb the surface uses (e.g. `Remove`). */
  confirmLabel?: string;
  /**
   * Runs on confirm. May be async — the buttons disable until it settles. A
   * rejection is *not* surfaced here; see the header.
   */
  onConfirm: () => void | Promise<void>;
  /** Controlled open state. Uncontrolled when omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
}

export function DeleteConfirmPopover({
  trigger,
  anchorOnly = false,
  title,
  description,
  confirmLabel = 'Delete',
  onConfirm,
  open,
  onOpenChange,
  align = 'end'
}: DeleteConfirmPopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [pending, setPending] = useState(false);

  const isControlled = open !== undefined && onOpenChange !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;
  const setOpen = isControlled ? onOpenChange : setUncontrolledOpen;

  const handleConfirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      /*
        Deliberately swallowed **here** and nowhere else: the caller threw it,
        so the caller already has it and owns reporting it (both Contacts hosts
        toast their delete failures). Letting it escape a DOM click handler
        would only produce an unhandled rejection — a worse outcome than a
        popover that stays open. What this component owes on failure is not
        stranding the user, which is what the `finally` below restores.
      */
    } finally {
      /*
        The popover usually unmounts with the record it just deleted, so this
        only matters on the failure path — where leaving the buttons disabled
        would strand the user with no way to retry or dismiss.
      */
      setPending(false);
    }
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => {
        // An in-flight delete owns the popover; dismissing mid-request would
        // leave the user with no signal that anything is still happening.
        if (pending) return;
        setOpen(next);
      }}
    >
      {anchorOnly ? (
        <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      {/*
        **The type scale is the surface's, not the framework default** (LF-6350 /
        [ATTR-8]). This shipped on `text-sm` / `text-xs`, which set the question at
        14px — a step *above* the 13px every value on the record behind it is set
        in, so the lightest confirmation in the product announced itself in larger
        type than the thing it was confirming. The prototype's scale is 13px for
        the question and 12px for the consequence: the value tier and the label
        tier, the same two-step relationship every attribute row already uses.

        Written as the literal `text-13px` / `text-12px` utilities rather than
        imported from `components/attributes/attribute-tokens.ts` — a `ui/` primitive
        importing out of a feature directory is the wrong direction, and the
        literal-named `--text-*px` scale in `globals.css` is itself the source of
        truth for type (see the frontend rules on design tokens not lying).
      */}
      <PopoverContent align={align} className="w-64 p-3">
        <p className="text-13px text-foreground">{title}</p>
        <p className="text-12px text-muted-foreground mt-1">{description}</p>
        <div className="mt-3 flex justify-end gap-2">
          <Button
            density="mid"
            type="button"
            variant="ghost"
            size="xs"
            disabled={pending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            density="mid"
            type="button"
            variant="destructive"
            size="xs"
            disabled={pending}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
